import React, { useState } from 'react';
import { useFloating, autoUpdate, offset, useInteractions, useHover, useDismiss, FloatingPortal } from "@floating-ui/react";
import { Localization } from "@viamap/viamap2-common";
import { PropertyInfoInterface } from "src/managers/PropertyInfoInterface";
import { PropertyData } from "../propertyInfoTemplates/PropertyInfoCollectorTypes";
import { ViewButtonIcon } from 'src/propertyInfoTemplates/PropertyComponents';
import { MIBIcon } from 'src/componentsUtils/MIBIcon';

// Utility function to get energy label color
export const getEnergyLabelColor = (classification: string) => {
   const mainLetter = classification.charAt(0);
   const yearMatch = classification.match(/(\d{4})/);
   const year = yearMatch ? yearMatch[1] : null;

   if (mainLetter === 'A' && year) {
      const yearKey = `A${year}`;
      const gradients = {
         'A2010': 'linear-gradient(to right, #01b63d, #61c525)',
         'A2015': 'linear-gradient(to right, #01b43e, #009b35)',
         'A2020': 'linear-gradient(to right, #00933a, #007f31)'
      };
      return gradients[yearKey] || 'linear-gradient(to right, #01b63d, #61c525)';
   }

   const gradients = {
      'A': 'linear-gradient(to right, #01b63d, #61c525)',
      'B': 'linear-gradient(to right, #6ac52a, #56a820)',
      'C': 'linear-gradient(to right, #e4c001, #e1a300)',
      'D': 'linear-gradient(to right, #ff9101, #e67d00)',
      'E': 'linear-gradient(to right, #ff551e, #e5481c)',
      'F': 'linear-gradient(to right, #d91424, #910006)',
      'G': 'linear-gradient(to right, #91020a, #67255a)'
   };
   return gradients[mainLetter] || '#ccc';
};

// Energy label icon component
export const EnergyLabelIcon = ({
   classification,
   size = 'medium'
}: {
   classification: string;
   size?: 'small' | 'medium' | 'large';
}) => {
   const mainLetter = classification.charAt(0);
   const yearMatch = classification.match(/(\d{4})/);
   const year = yearMatch ? yearMatch[1] : null;

   const [tooltipOpen, setTooltipOpen] = useState(false);
   const { refs, floatingStyles, context: tooltipContext } = useFloating({
      whileElementsMounted: autoUpdate,
      open: tooltipOpen,
      middleware: [offset(5)],
      onOpenChange: setTooltipOpen,
   });
   const {
      getFloatingProps: getTooltipFloatingProps
   } = useInteractions([useHover(tooltipContext), useDismiss(tooltipContext)]);

   const sizeMap = {
      small: { width: '24px', height: '24px', fontSize: '12px', yearFontSize: '8px', marginTop: '-1px' },
      medium: { width: '40px', height: '40px', fontSize: '16px', yearFontSize: '10px', marginTop: '-4px' },
      large: { width: '60px', height: '60px', fontSize: '24px', yearFontSize: '14px', marginTop: '-8px' }
   };

   const dimensions = sizeMap[size];

   return (
      <>
         <div
            ref={refs.setReference}
            style={{
               background: getEnergyLabelColor(classification),
               color: 'white',
               width: dimensions.width,
               height: dimensions.height,
               borderRadius: '8px',
               display: 'flex',
               flexDirection: 'column',
               alignItems: 'center',
               justifyContent: 'center',
               fontWeight: 'bold',
               fontSize: year ? dimensions.fontSize : dimensions.fontSize,
               margin: '0',
               boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
               cursor: 'pointer'
            }}
         >
            <div style={{ fontSize: year ? dimensions.fontSize : dimensions.fontSize, lineHeight: '1' }}>
               {mainLetter}
            </div>
            {year && (
               <div style={{ fontSize: dimensions.yearFontSize, lineHeight: '1', marginTop: dimensions.marginTop }}>
                  {year}
               </div>
            )}
         </div>
         {Boolean(classification && tooltipOpen) && (
            <FloatingPortal>
               <div ref={refs.setFloating} className="ViewButtonToolTip" style={floatingStyles} {...getTooltipFloatingProps()}>
                  {classification}
               </div>
            </FloatingPortal>
         )}
      </>
   );
};

export const filterLatestEnergyLabels = (labels: any[]) => {
   const buildingMap = new Map();

   labels.forEach(label => {
      const normalizedBuildingNumber = label.Buildings[0]?.BuildingNumber?.replace(/^0+/, '') || 'N/A';
      const buildingKey = `${normalizedBuildingNumber}`;
      const validFromDate = new Date(label.ValidFrom.split('-').reverse().join('-'));

      if (!buildingMap.has(buildingKey) || validFromDate > new Date(buildingMap.get(buildingKey).ValidFrom.split('-').reverse().join('-'))) {
         buildingMap.set(buildingKey, label);
      }
   });

   return Array.from(buildingMap.values());
};

// Energy label summary component for titles
export const EnergyLabelSummary = ({ data }: { data: PropertyData }) => {
   if (!data.EnergyLabel || !data.EnergyLabel.EnergyLabels || data.EnergyLabel.EnergyLabels.length === 0) {
      return null;
   }

   const getSortKey = (classification: string) => {

      const order = [
         "A2030", "A2025", "A2020", "A2015", "A2010", "A", "B", "C", "D", "E", "F", "G"
      ];
      const upper = classification.toUpperCase();
      const idx = order.findIndex(o => upper.startsWith(o));
      return idx === -1 ? order.length : idx;
   };
   const filteredLabels = filterLatestEnergyLabels(data.EnergyLabel.EnergyLabels);
   const uniqueLabelsMap = new Map<string, any>();
   filteredLabels.forEach(label => {
      const classKey = label.EnergyLabelClassification.toUpperCase();
      if (!uniqueLabelsMap.has(classKey)) {
         uniqueLabelsMap.set(classKey, label);
      }
   });

   const uniqueLabels = Array.from(uniqueLabelsMap.values());
   const sortedLabels = uniqueLabels.sort((a, b) => getSortKey(a.EnergyLabelClassification) - getSortKey(b.EnergyLabelClassification));

   return (
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginLeft: '8px' }}>
         {sortedLabels.map((label) => (
            <EnergyLabelIcon
               key={label.EnergyLabelClassification}
               classification={label.EnergyLabelClassification}
               size="small"
            />
         ))}
      </div>
   );
};

// PDF download button component
export const EnergyLabelPdfButton = ({ identifier }: { identifier: string }) => {
   const [downloadStatus, setDownloadStatus] = useState<'idle' | 'loading' | 'error'>('idle');

   const handleClick = async () => {
      setDownloadStatus('loading');

      const pdfBlob = await PropertyInfoInterface.getEnergyLabelPdf(identifier);

      if (pdfBlob && pdfBlob.size > 0) {
         setDownloadStatus('idle');
         const url = window.URL.createObjectURL(pdfBlob);
         window.open(url, '_blank');
      } else {
         setDownloadStatus('error');
      }
   };

   if (downloadStatus === 'error') {
      return (
         <span style={{ color: 'red', fontSize: '12px' }}>
            {Localization.getText("PDF not available")}
         </span>
      );
   }

   return (
      <ViewButtonIcon
         onClick={handleClick}
         title={Localization.getText("View PDF")}
         disabled={downloadStatus === 'loading'}
         style={{
            backgroundColor: 'transparent',
            transition: 'background 0.2s',
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
         }}
         onMouseOver={e => (e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.01)')}
         onMouseOut={e => (e.currentTarget.style.backgroundColor = 'transparent')}
      >
         <MIBIcon icon="pdf" color="green" />
      </ViewButtonIcon>
   );
};